import { type FormEvent, useState } from 'react';
import {
  onHub,
  posDevice,
  requestPasswordReset,
  sendSignInLink,
  signInStaff,
  signInWithEmailAndPin,
  signInWithPin,
  tillApi,
} from '../../infra/session';
import { ErrorBox, Field, PinPad } from '../../ui/components';
import { Icon } from '../../ui/icons';

/** The brand side of every sign-in, PIN, setup and loading screen: solid brand colour, logo, name. */
export function BrandPanel({ note }: { note?: string }) {
  return (
    <aside className="brand-panel">
      <img src="/logo-512.png" alt="MY FOOD — Chefelisha Restaurant" />
      <strong className="name">Chefelisha Restaurant</strong>
      <span className="slogan">Food is better than love</span>
      {note ? <span className="note">{note}</span> : null}
    </aside>
  );
}

export function Brand() {
  return (
    <div className="auth-brand">
      <img className="brand-logo" src="/logo-192.png" alt="MY FOOD — Chefelisha Restaurant" />
      <span className="brand-title">Chefelisha Restaurant</span>
    </div>
  );
}

/**
 * Sign-in is PIN first. On a device MY FOOD knows (a paired till, or an owner's / manager's own
 * phone or laptop) only the PIN is asked. On a new device an owner or manager enters email + PIN
 * once. Email and password remain available for support accounts.
 */
export function LoginScreen({ onSignedIn }: { onSignedIn: () => void }) {
  const till = posDevice();
  const [mode, setMode] = useState<'pin' | 'email' | 'forgot-pin' | 'setup'>(
    till?.pinReady ? 'pin' : 'setup',
  );
  if (onHub) return <HubLogin tillName={till?.pinReady ? till.name : null} onSignedIn={onSignedIn} />;
  return (
    <div className="auth">
      <BrandPanel />
      <div className="auth-card">
        <Brand />
        {mode === 'pin' && till ? (
          <PinSignIn
            tillName={till.name}
            onSignedIn={onSignedIn}
            onEmail={() => setMode('email')}
            onForgot={() => setMode('forgot-pin')}
          />
        ) : mode === 'forgot-pin' ? (
          <ForgotPin onBack={() => setMode('pin')} />
        ) : mode === 'setup' ? (
          <SetUpThisDevice onSignedIn={onSignedIn} onPassword={() => setMode('email')} />
        ) : (
          <EmailSignIn
            tillName={till?.name ?? null}
            onSignedIn={onSignedIn}
            onPin={till?.pinReady ? () => setMode('pin') : null}
          />
        )}
      </div>
    </div>
  );
}

/**
 * A browser MY FOOD does not know yet. Owners and managers sign in with their email and PIN; this
 * device then remembers them and asks only for the PIN. Restaurant tills are paired by a manager.
 */
function SetUpThisDevice({ onSignedIn, onPassword }: { onSignedIn: () => void; onPassword: () => void }) {
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [forgot, setForgot] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithEmailAndPin(email.trim(), pin);
      onSignedIn();
    } catch (err) {
      setError(err);
      setPin('');
    } finally {
      setBusy(false);
    }
  }
  if (forgot) return <ForgotOwnerPin email={email} onBack={() => setForgot(false)} />;
  return (
    <>
      <h1>Sign in</h1>
      <form className="form" onSubmit={submit}>
        <Field label="Email">
          <input
            type="email"
            autoComplete="username"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="PIN">
          <input
            type="password"
            inputMode="numeric"
            autoComplete="current-password"
            pattern="[0-9]{4,6}"
            maxLength={6}
            required
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
        <ErrorBox error={error} />
        <button className="btn primary lg block" disabled={busy || pin.length < 4} type="submit">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="muted small">Next time on this device, just enter your PIN.</p>
      <div className="auth-foot">
        <button type="button" className="link" onClick={() => setForgot(true)}>
          Forgot PIN?
        </button>
        <a href="/pair">Set up a restaurant till</a>
      </div>
      <div className="auth-foot">
        <button type="button" className="link" onClick={onPassword}>
          Sign in with email and password
        </button>
      </div>
    </>
  );
}

/** Forgotten PIN (owners and managers): an emailed link to choose a new one. */
function ForgotOwnerPin({ email: initial, onBack }: { email: string; onBack: () => void }) {
  const [email, setEmail] = useState(initial);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await sendSignInLink(email.trim());
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Forgot your PIN?</h1>
      {sent ? (
        <p className="lead">
          If <strong>{email.trim()}</strong> belongs to an owner or manager, a link to choose a new PIN is on
          its way. Another owner or manager can also reset your PIN in Staff.
        </p>
      ) : (
        <form className="form" onSubmit={submit}>
          <Field label="Your email" hint="We email you a link to choose a new PIN.">
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <ErrorBox error={error} />
          <button className="btn primary lg block" disabled={busy} type="submit">
            {busy ? 'Sending…' : 'Email me a link'}
          </button>
        </form>
      )}
      <div className="auth-foot">
        <button type="button" className="link" onClick={onBack}>
          Back to sign in
        </button>
      </div>
    </>
  );
}

/**
 * On the in-store hub, tills unlock with a PIN only (managers use the cloud back office for email
 * sign-in; forgotten PINs are reset there too). A screen that is not paired yet goes to pairing.
 */
function HubLogin({ tillName, onSignedIn }: { tillName: string | null; onSignedIn: () => void }) {
  return (
    <div className="auth">
      <BrandPanel />
      <div className="auth-card">
        <Brand />
        {tillName ? (
          <PinSignIn tillName={tillName} onSignedIn={onSignedIn} onEmail={null} onForgot={null} />
        ) : (
          <>
            <h1>Connect this screen</h1>
            <p className="lead">
              This device is not connected to the MY FOOD Hub yet. It shows a code; a manager approves it on
              the hub computer.
            </p>
            <a className="btn primary xl block" href="/pair">
              Connect this device
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function PinSignIn({
  tillName,
  onSignedIn,
  onEmail,
  onForgot,
}: {
  tillName: string;
  onSignedIn: () => void;
  onEmail: (() => void) | null;
  onForgot: (() => void) | null;
}) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [shake, setShake] = useState(false);
  async function submit() {
    if (pin.length < 4 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await signInWithPin(pin);
      onSignedIn();
    } catch (e) {
      setError(e);
      setPin('');
      setShake(true);
      setTimeout(() => setShake(false), 350);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Enter your staff PIN</h1>
      <p className="lead">{tillName}</p>
      <PinPad
        value={pin}
        onChange={(v) => {
          setError(null);
          setPin(v);
        }}
        onSubmit={() => void submit()}
        disabled={busy}
        shake={shake}
      />
      <ErrorBox error={error} />
      <button
        type="button"
        className="btn primary xl block"
        disabled={pin.length < 4 || busy}
        onClick={() => void submit()}
      >
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      {onForgot && onEmail ? (
        <div className="auth-foot">
          <button type="button" className="link" onClick={onForgot}>
            Forgot PIN?
          </button>
          <button type="button" className="link" onClick={onEmail}>
            Sign in with email and password
          </button>
        </div>
      ) : (
        <p className="small muted center-text">
          Forgot your PIN? A manager can reset it in the MY FOOD back office.
        </p>
      )}
    </>
  );
}

function ForgotPin({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await tillApi.pinRecovery(email.trim());
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Forgot your PIN?</h1>
      {sent ? (
        <div className="info-box ok" role="status">
          If <strong>{email.trim()}</strong> belongs to a staff member here, a link to choose a new PIN is on
          its way. Your old PIN stops working once you set the new one. A manager can also reset it for you.
        </div>
      ) : (
        <form className="form" onSubmit={submit}>
          <p className="lead">We will email a link to the address your manager registered for you.</p>
          <Field label="Your email" required>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <ErrorBox error={error} />
          <button type="submit" className="btn primary lg block" disabled={busy}>
            {busy ? 'Sending…' : 'Email me a link'}
          </button>
        </form>
      )}
      <div className="auth-foot">
        <button type="button" className="link" onClick={onBack}>
          <Icon name="arrow-left" size={16} /> Back to PIN
        </button>
      </div>
    </>
  );
}

function EmailSignIn({
  tillName,
  onSignedIn,
  onPin,
}: {
  tillName: string | null;
  onSignedIn: () => void;
  onPin: (() => void) | null;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [resetSent, setResetSent] = useState(false);

  async function forgot() {
    if (!email.trim()) {
      setError(new Error('Enter your email first, then press "Forgot password?"'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      setResetSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInStaff(email.trim(), password);
      onSignedIn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Sign in</h1>
      {tillName ? <p className="lead">This till: {tillName}</p> : null}
      <form className="form" onSubmit={submit}>
        <Field label="Email">
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        <ErrorBox error={error} />
        <button className="btn primary lg block" disabled={busy} type="submit">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      {resetSent ? (
        <div className="info-box ok" role="status">
          If {email.trim()} has an account, an email with a link to set a new password is on its way.
        </div>
      ) : null}
      <div className="auth-foot">
        {resetSent ? (
          <span />
        ) : (
          <button type="button" className="link" disabled={busy} onClick={() => void forgot()}>
            Forgot password?
          </button>
        )}
        {onPin ? (
          <button type="button" className="link" onClick={onPin}>
            Staff PIN sign-in
          </button>
        ) : (
          <a href="/pair">Set up this device</a>
        )}
      </div>
    </>
  );
}
